import type { PosKey } from '@/types/sentence'
import { cssVar } from '@/utils/cssVar'

export interface PosStyle {
  label: string
  bg: string
  border: string
  text: string
  /** The chip's border colour as a fill, for a solid rule or spine in the POS hue.
   *  `bg` is a near-white wash in light mode and vanishes against a card. */
  spine: string
}

const styles: Record<PosKey, PosStyle> = {
  noun: { label: 'Noun', bg: 'bg-pos-noun-bg', border: 'border-pos-noun-border', text: 'text-pos-noun-text', spine: 'bg-pos-noun-border' },
  verb: { label: 'Verb', bg: 'bg-pos-verb-bg', border: 'border-pos-verb-border', text: 'text-pos-verb-text', spine: 'bg-pos-verb-border' },
  adj: { label: 'Adjective', bg: 'bg-pos-adj-bg', border: 'border-pos-adj-border', text: 'text-pos-adj-text', spine: 'bg-pos-adj-border' },
  adv: { label: 'Adverb', bg: 'bg-pos-adv-bg', border: 'border-pos-adv-border', text: 'text-pos-adv-text', spine: 'bg-pos-adv-border' },
  prep: { label: 'Preposition', bg: 'bg-pos-prep-bg', border: 'border-pos-prep-border', text: 'text-pos-prep-text', spine: 'bg-pos-prep-border' },
  pron: { label: 'Pronoun', bg: 'bg-pos-pron-bg', border: 'border-pos-pron-border', text: 'text-pos-pron-text', spine: 'bg-pos-pron-border' },
  conj: { label: 'Conjunction', bg: 'bg-pos-conj-bg', border: 'border-pos-conj-border', text: 'text-pos-conj-text', spine: 'bg-pos-conj-border' },
  part: { label: 'Particle', bg: 'bg-pos-other-bg', border: 'border-pos-other-border', text: 'text-pos-other-text', spine: 'bg-pos-other-border' },
  num: { label: 'Numeral', bg: 'bg-pos-other-bg', border: 'border-pos-other-border', text: 'text-pos-other-text', spine: 'bg-pos-other-border' },
  intj: { label: 'Interjection', bg: 'bg-pos-other-bg', border: 'border-pos-other-border', text: 'text-pos-other-text', spine: 'bg-pos-other-border' },
}

export function posStyle(pos: PosKey): PosStyle {
  return styles[pos] ?? styles.noun
}

export interface PosSvgColors {
  stroke: string
  fill: string
  text: string
}

/**
 * Which `--color-pos-*` chip token each POS maps to, plus the legacy hexes used
 * only as a fallback when the CSS variable is unset (SSR / tests).
 */
const SVG_SOURCE: Record<PosKey, { token: string; stroke: string; fill: string; text: string }> = {
  noun: { token: 'noun', stroke: '#c3aede', fill: '#efe9f5', text: '#5c4080' },
  verb: { token: 'verb', stroke: '#7fcfa9', fill: '#dcefe5', text: '#0b6f4c' },
  adj: { token: 'adj', stroke: '#e3c98a', fill: '#f7ecd0', text: '#8a6516' },
  adv: { token: 'adv', stroke: '#9fbcd9', fill: '#e4edf5', text: '#2c5680' },
  prep: { token: 'prep', stroke: '#aaa0d4', fill: '#e8e5f3', text: '#463c7a' },
  pron: { token: 'pron', stroke: '#aab6cc', fill: '#e7ebf2', text: '#3a4a6b' },
  conj: { token: 'conj', stroke: '#8ac7bd', fill: '#dceeea', text: '#166055' },
  part: { token: 'other', stroke: '#c3cac6', fill: '#eef0ee', text: '#55616a' },
  num: { token: 'other', stroke: '#c3cac6', fill: '#eef0ee', text: '#55616a' },
  intj: { token: 'other', stroke: '#c3cac6', fill: '#eef0ee', text: '#55616a' },
}

/**
 * Colours for the inline SVG arc diagram. SVG attributes can't use Tailwind
 * utilities, so the resolved values are read from the same `--color-pos-*` chip
 * tokens the DOM chips use (ADR-005) — keeping the SVG in lock-step with the
 * theme. Read at render time; the caller re-renders on theme toggle.
 */
export function posSvgColors(pos: PosKey): PosSvgColors {
  const src = SVG_SOURCE[pos] ?? SVG_SOURCE.noun
  return {
    stroke: cssVar(`--color-pos-${src.token}-border`, src.stroke),
    fill: cssVar(`--color-pos-${src.token}-bg`, src.fill),
    text: cssVar(`--color-pos-${src.token}-text`, src.text),
  }
}
